export type ScoutCriterionId =
  | 'location'
  | 'jobTitle'
  | 'experience'
  | 'industry'
  | 'skills';

export interface ScoutCriterion {
  id: ScoutCriterionId;
  label: string;
  hint: string;
  detect: (text: string) => boolean;
}

const wordList = (words: string[]) =>
  new RegExp(`\\b(?:${words.join('|')})\\b`, 'i');

const LOCATION_KEYWORDS = wordList([
  'remote',
  'hybrid',
  'on-?site',
  'relocat\\w*',
  'london',
  'berlin',
  'munich',
  'prague',
  'paris',
  'madrid',
  'barcelona',
  'amsterdam',
  'dublin',
  'lisbon',
  'warsaw',
  'vienna',
  'zurich',
  'milan',
  'rome',
  'stockholm',
  'copenhagen',
  'oslo',
  'helsinki',
  'brussels',
  'budapest',
  'new york',
  'san francisco',
  'los angeles',
  'boston',
  'chicago',
  'seattle',
  'austin',
  'toronto',
  'singapore',
  'dubai',
  'bangalore',
  'sydney',
  'tokyo',
  'uk',
  'usa',
  'germany',
  'france',
  'spain',
  'italy',
  'poland',
  'netherlands',
  'sweden',
  'ireland',
  'portugal',
  'czechia',
  'switzerland',
  'eu',
  'emea',
  'apac',
]);

// "in Prague", "near Munich", "based in Berlin" — preposition + capitalised place.
const LOCATION_PHRASE =
  /\b(?:in|near|around|based in|relocating to|outside)\s+[A-Z][a-z]+/;

const JOB_TITLE_KEYWORDS = wordList([
  'engineer',
  'developer',
  'designer',
  'architect',
  'manager',
  'lead',
  'director',
  'analyst',
  'scientist',
  'consultant',
  'recruiter',
  'accountant',
  'marketer',
  'specialist',
  'coordinator',
  'administrator',
  'nurse',
  'teacher',
  'lawyer',
  'controller',
  'strategist',
  'researcher',
  'representative',
  'executive',
  'officer',
  'sales',
  'marketing',
  'account executive',
  'product manager',
  'project manager',
  'software',
  'frontend',
  'front-end',
  'backend',
  'back-end',
  'fullstack',
  'full-stack',
  'devops',
  'ae',
  'sdr',
  'bdr',
  'pm',
  'cto',
  'cfo',
  'ceo',
  'cmo',
  'vp',
]);

// "3 years", "3+ years", "3+ yrs", "5y experience"
const EXPERIENCE_PATTERN = /\b\d+\s*\+?\s*(?:years?|yrs?|y)\b/i;

const INDUSTRY_KEYWORDS = wordList([
  'fintech',
  'saas',
  'b2b',
  'b2c',
  'health-?(?:care|tech)',
  'medtech',
  'biotech',
  'e-?commerce',
  'edtech',
  'insur(?:ance|tech)',
  'bank(?:ing)?',
  'retail',
  'hospitality',
  'gaming',
  'automotive',
  'manufacturing',
  'logistics',
  'real estate',
  'proptech',
  'martech',
  'adtech',
  'cyber-?security',
  'crypto',
  'web3',
  'blockchain',
  'telecom\\w*',
  'energy',
  'pharma\\w*',
  'media',
  'consulting',
  'legaltech',
  'hrtech',
  'travel',
  'aerospace',
  'defen[cs]e',
  'agency',
]);

const SKILL_KEYWORDS = wordList([
  'react',
  'vue',
  'angular',
  'svelte',
  'next\\.?js',
  'node\\.?js',
  'node',
  'typescript',
  'javascript',
  'python',
  'java',
  'kotlin',
  'swift',
  'golang',
  'rust',
  'ruby',
  'php',
  'c\\+\\+',
  'c#',
  '\\.net',
  'sql',
  'graphql',
  'aws',
  'gcp',
  'azure',
  'kubernetes',
  'docker',
  'terraform',
  'salesforce',
  'hubspot',
  'tableau',
  'figma',
  'excel',
  'sap',
  'snowflake',
  'spark',
  'tensorflow',
  'pytorch',
]);

// "experience with X", "proficient in X", "skilled in X"
const SKILL_PHRASE =
  /\b(?:proficien\w+|skilled in|expertise in|hands-on|familiar with|knowledge of|experienced? (?:with|in))\b/i;

export const SCOUT_CRITERIA: ScoutCriterion[] = [
  {
    id: 'location',
    label: 'Location',
    hint: 'Where they should be based or whether remote works',
    detect: (text) =>
      LOCATION_KEYWORDS.test(text) || LOCATION_PHRASE.test(text),
  },
  {
    id: 'jobTitle',
    label: 'Job Title',
    hint: 'The role or function you are hiring for',
    detect: (text) => JOB_TITLE_KEYWORDS.test(text),
  },
  {
    id: 'experience',
    label: 'Years of Experience',
    hint: 'How much experience the role needs',
    detect: (text) => EXPERIENCE_PATTERN.test(text),
  },
  {
    id: 'industry',
    label: 'Industry',
    hint: 'The market or domain they should come from',
    detect: (text) => INDUSTRY_KEYWORDS.test(text),
  },
  {
    id: 'skills',
    label: 'Skills',
    hint: 'Tools, technologies, or strengths that matter',
    detect: (text) => SKILL_KEYWORDS.test(text) || SKILL_PHRASE.test(text),
  },
];

export const detectCriteria = (prompt: string): Set<ScoutCriterionId> => {
  const detected = new Set<ScoutCriterionId>();
  if (!prompt.trim()) {
    return detected;
  }
  for (const criterion of SCOUT_CRITERIA) {
    if (criterion.detect(prompt)) {
      detected.add(criterion.id);
    }
  }
  return detected;
};
